import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './index.module.css';
import { GroupProduct, Product, ProductType } from '@meitu/subscribe-core';
import Swiper from '@/assets/js/swiper-bundle';
import { getDisplayPrice, getDurationPeriod, getIncomeSource } from '@/utils/vip';
import { getSkuType, getUrlParam } from '@/utils';
import {
  SubscribeType,
  isTeamSubscription,
  subscribeType,
  purchaseScene,
  PurchaseScene,
} from '@/constants';
import { PromotionType, TabType } from '@/constants/subscription';
import { useAtom } from 'jotai';
import {
  activeIndexAtom,
  curProductAtom,
  curTabAtom,
  loadingAtom,
  productsAtom,
  seatsAtom,
  teamInfoAtom,
  vipInfoAtom,
  visibleServiceNoticeAtom,
} from '@/store';
import { track } from '@/utils/common';

const ProductList = () => {
  const [loading] = useAtom(loadingAtom);
  const [products] = useAtom(productsAtom);
  const [activeIndex, setActiveIndex] = useAtom(activeIndexAtom);
  const [curProduct] = useAtom(curProductAtom);
  const [curTab] = useAtom(curTabAtom);
  const [vipInfo] = useAtom(vipInfoAtom);
  const [teamInfo] = useAtom(teamInfoAtom);
  const [, setSeats] = useAtom(seatsAtom);
  const [, setVisibleServiceNotice] = useAtom(visibleServiceNoticeAtom);

  const [showLeftPannel, setShowLeftPannel] = useState<boolean>(false);
  const [showRightPannel, setShowRightPannel] = useState<boolean>(false);
  const ref = useRef<any>();
  const type: any = getUrlParam('type');
  // web侧业务通过hideLowPriceTrialCopywriting来控制是否展示0.9卡片上是文案，PC端环境则全部隐藏
  // const hideLowPriceTrialCopywriting: any =
  //   +(getUrlParam('hideLowPriceTrialCopywriting') || '') || getIncomeSource() !== 'MTXX-WEB';
  // 12月份PC端内嵌的临时放开，冲刺完成OKR
  const hideLowPriceTrialCopywriting: any = +(getUrlParam('hideLowPriceTrialCopywriting') || '');

  const isBigCard = products.length && products.length < 4;

  const handleSelectChange = (index: number) => {
    if (loading) return;

    setActiveIndex(index);

    const current = products[index] as Product;

    // 设置默认席位数为商品的最小席位数
    if (isTeamSubscription) {
      const lowerLimit = (current as GroupProduct)?.seatInfo?.lowerLimit;
      // 当前团队绑定的成员数
      const userCount = teamInfo?.member_detail?.user_count || 0;
      lowerLimit && setSeats(current.isProbation ? lowerLimit : Math.max(lowerLimit, userCount));
    }

    // 6是立即赠送，匹配会员买赠信息
    const promotion: any = current?.promotions?.find(
      (item) => item.promotionType === PromotionType.Accessory
    );
    // 普通会员商品配置的买赠活动信息
    const promotionGiftForConsumable = promotion?.promotionGifts?.find(
      (item: any) => item.productType === ProductType.Consumable
    );

    let buy_num: any = '';
    if (current?.productType === ProductType.Consumable) {
      // 购买张数
      buy_num = current?.commodityConfig?.count || current?.meidouRights?.count;
    } else if (promotionGiftForConsumable) {
      // 会员买赠
      buy_num = promotionGiftForConsumable?.commodityConfig?.count;
    }

    let params: any = {};
    if (getIncomeSource() === 'MTXX-WEB') {
      params = {
        class: 'name_id',
        name_id: current?.productId,
        country_code: current?.countryCode || '',
        sku_type: getSkuType(),
        buy_num,
      };
    } else {
      params = {
        category: current?.productName,
        product_id: current?.productId,
        country_code: current?.countryCode || '',
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });
  };

  useEffect(() => {
    if (ref.current && activeIndex > -1) {
      ref.current.slideTo(activeIndex);
    }
  }, [activeIndex]);

  useEffect(() => {
    if (products.length < 4) return;

    if (ref.current) ref.current.destroy();
    initSwiper(activeIndex);
  }, [products]);

  const handlePannelStatus = (swiper: any) => {
    if (swiper.isBeginning) {
      setShowLeftPannel(false);
    } else {
      setShowLeftPannel(true);
    }

    if (swiper.isEnd) {
      setShowRightPannel(false);
    } else {
      setShowRightPannel(true);
    }
  };

  const initSwiper = (initedIndex: number) => {
    if (ref.current) ref.current.destroy();

    ref.current = new Swiper('.swiper-container-new', {
      slidesPerView: products.length < 4 ? 3.08 : 3.5,
      slidesPerGroup: 3,
      // spaceBetween: 20,
      loop: false, // 关闭循环模式
      initialSlide: initedIndex,
      allowTouchMove: false,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      on: {
        slideChangeTransitionEnd: function (swiper: any) {
          handlePannelStatus(swiper);
        },
      },
    });

    handlePannelStatus(ref.current);
  };

  const renderArrow = (showArrow: boolean) => {
    if (products.length < 4) return;

    return showArrow ? (
      <>
        {/* <div
          className={classNames(styles.leftPannel, showLeftPannel ? styles.show : styles.hide)}
        ></div> */}
        <div className={classNames('swiper-button-prev', styles.arrow)}></div>
        {/* <div
          className={classNames(styles.rightPannel, showRightPannel ? styles.show : styles.hide)}
        ></div> */}
        <div className={classNames('swiper-button-next', styles.arrow, styles.arrowRight)}></div>
      </>
    ) : (
      <div className={classNames(styles.rightPannel)}></div>
    );
  };

  // 美豆类型且购买美豆tab
  const renderMeidouType = (data: Product) => {
    const count = data.meidouRights?.count || 0;
    // 大额美豆(大于等于10w)展示需要处理下
    const countText = count >= 100000 ? `${count / 10000}万` : count;
    return (
      <>
        <div className={classNames(styles.name)}>{countText}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          {getDisplayPrice(data).price}
        </div>
        {/* 原价xxxxx */}
        {data?.productPrice.originalPrice ? (
          <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
            getDisplayPrice(data).originalPrice
          }`}</div>
        ) : null}
        {/* 促销文案 */}
        {data?.productDesc ? (
          <div className={classNames(styles.tips, isBigCard && styles.bigCardTips)}>
            {data.productDesc}
          </div>
        ) : null}

        {/* 左上角tag */}
        {data.commonDesc ? <div className={styles.tag}>{data.commonDesc}</div> : null}
      </>
    );
  };

  // 抠图单购商品
  const renderConsumableType = (data: Product) => {
    const priceUnit = data?.commodityConfig?.count
      ? getDisplayPrice(data).price / data?.commodityConfig?.count
      : 0;
    return (
      <>
        <div className={styles.name}>{data.productName}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          {priceUnit.toFixed(2)}/张
        </div>

        <div className={styles.desc} style={{ textDecoration: 'none' }}>{`${
          getDisplayPrice(data).moneySymbol || ''
        }${getDisplayPrice(data).price}`}</div>

        {/* 张数商品展示有效期 */}
        <div className={classNames(styles.tips, isBigCard && styles.bigCardTips)}>
          {getDurationPeriod(data.commodityConfig)}
        </div>
        {data.promotionBanner ? <div className={styles.tag}>{data.promotionBanner}</div> : null}
      </>
    );
  };

  // 续期/非续期商品
  const renderSubscriptionType = (data: Product) => {
    // 取第一个优惠信息
    const promotionInfo: any = data?.promotions && data.promotions[0];
    // 是否是低价试用商品
    const isLowPriceRrialProduct =
      promotionInfo?.promotionType === PromotionType.LowPriceTrialPromotion;

    const renderDesc = () => {
      // 续期商品
      if (
        data.productType === ProductType.RenewedSubscription &&
        curProduct?.productId === data.productId &&
        data.checkBox?.explain
      ) {
        return (
          <div className={classNames(styles.tips, isBigCard && styles.bigCardTips)}>
            <span>{data.checkBox?.explain}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              onClick={() => setVisibleServiceNotice(true)}
            >
              <g clipPath="url(#clip0_41525_180686)">
                <path
                  d="M7.10905 9.07357C7.10905 9.41012 6.83622 9.68294 6.49967 9.68294C6.16313 9.68294 5.8903 9.41012 5.8903 9.07357C5.8903 8.73702 6.16313 8.46419 6.49967 8.46419C6.83622 8.46419 7.10905 8.73702 7.10905 9.07357Z"
                  fill="#FF9D65"
                />
                <path
                  d="M6.56173 6.40526C6.29936 6.72139 6.15754 7.064 6.09991 7.46906C6.06495 7.71477 6.14087 7.94813 6.45306 7.98732C6.67039 8.01459 6.91488 7.8472 6.91488 7.63272C6.91488 7.41824 6.99156 7.17305 7.15937 7.03263C7.29917 6.89226 7.43948 6.77255 7.57407 6.65771C7.95325 6.33419 8.28707 6.04938 8.43616 5.45058C8.51765 5.01415 8.40899 4.57772 8.19167 4.2504C7.97434 3.92308 7.64835 3.65031 7.26803 3.51393C6.91488 3.37754 6.50739 3.35027 6.12707 3.4321C5.36644 3.59576 4.71445 4.22312 4.55147 5.01415C4.48201 5.35126 4.65387 5.5802 4.95895 5.61424C5.20456 5.64164 5.46542 5.48258 5.50226 5.20509C5.5566 4.79594 5.90975 4.44134 6.31723 4.33223C6.75189 4.22312 7.2137 4.38678 7.43103 4.74138C7.67702 5.11188 7.56151 5.5105 7.26803 5.80517C7.18654 5.90064 7.07787 5.98247 6.95563 6.07453C6.83338 6.16659 6.69755 6.26888 6.56173 6.40526Z"
                  fill="#FF9D65"
                />
                <path
                  d="M0.676758 6.50065C0.676758 3.28474 3.28377 0.677734 6.49967 0.677734C9.71558 0.677734 12.3226 3.28474 12.3226 6.50065C12.3226 9.71656 9.71558 12.3236 6.49967 12.3236C3.28377 12.3236 0.676758 9.71656 0.676758 6.50065ZM6.49967 1.49023C3.7325 1.49023 1.48926 3.73347 1.48926 6.50065C1.48926 9.26783 3.7325 11.5111 6.49967 11.5111C9.26685 11.5111 11.5101 9.26783 11.5101 6.50065C11.5101 3.73347 9.26685 1.49023 6.49967 1.49023Z"
                  fill="#FF9D65"
                />
              </g>
              <defs>
                <clipPath id="clip0_41525_180686">
                  <rect width="13" height="13" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
        );
      }

      if (data.productDesc) {
        return (
          <div className={classNames(styles.tips, isBigCard && styles.bigCardTips)}>
            {data.productDesc}
          </div>
        );
      }
      return null;
    };

    return (
      <>
        <div className={styles.name}>{data.productName}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          {isLowPriceRrialProduct && !hideLowPriceTrialCopywriting
            ? getDisplayPrice(data).payPrice
            : getDisplayPrice(data).price}
          {isLowPriceRrialProduct && !hideLowPriceTrialCopywriting ? (
            <span className={styles.priceDesc}>试用7天</span>
          ) : null}
        </div>

        {getDisplayPrice(data).originalPrice ? (
          <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
            getDisplayPrice(data).originalPrice
          }`}</div>
        ) : null}

        {/* 会员商品展示商品文案横条 */}
        {renderDesc()}
        {data.promotionBanner ? <div className={styles.tag}>{data.promotionBanner}</div> : null}
      </>
    );
  };

  const renderTeamSubscriptionType = (data: GroupProduct) => {
    // 取第一个优惠信息
    const promotionInfo: any = data?.promotions && data.promotions[0];
    // 是否是阶梯优惠商品类型
    const isTieredDiscounts = promotionInfo?.promotionType === PromotionType.TieredDiscounts;

    if (isTieredDiscounts) {
      return (
        <>
          <div className={styles.name}>{data.productName}</div>
          <div className={styles.price}>
            <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
            {+(data.promoteProductPrice.price || 0) / 100}
          </div>

          {getDisplayPrice(data).originalPrice && purchaseScene === PurchaseScene.NORMAL ? (
            <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
              (+(data.productPrice.price || 0) / 100) * (promotionInfo.enjoyNum || 0)
            }`}</div>
          ) : null}
          {/* 中台暂不支持，前端先写死 */}
          <div className={classNames(styles.tips, isBigCard && styles.bigCardTips)}>
            企业商用授权
          </div>
          <div className={styles.tag}>限时优惠</div>
        </>
      );
    }

    // isProbation 标识团队商品是否为低价试用商品
    return (
      <>
        <div className={styles.name}>{data.productName}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          {data.isProbation
            ? +(getDisplayPrice(data).price * data.seatInfo.lowerLimit).toFixed(2)
            : getDisplayPrice(data).price}
          <span className={styles.unit}>
            /{data.isProbation ? data.seatInfo.lowerLimit : ''}席位
          </span>
        </div>

        {getDisplayPrice(data).originalPrice && purchaseScene === PurchaseScene.NORMAL ? (
          <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
            getDisplayPrice(data).originalPrice
          }`}</div>
        ) : null}

        {/* 会员商品展示商品文案横条 */}
        {data.productDesc ? (
          <div className={classNames(styles.tips, isBigCard && styles.bigCardTips)}>
            {data.productDesc}
          </div>
        ) : null}
        {data.promotionBanner ? <div className={styles.tag}>{data.promotionBanner}</div> : null}
      </>
    );
  };

  // 批处理的消耗商品
  const renderBatchType = (data: Product) => {
    return (
      <>
        <div className={styles.name}>{data.productName}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          {getDisplayPrice(data).price}
        </div>
        {getDisplayPrice(data).originalPrice ? (
          <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
            getDisplayPrice(data).originalPrice
          }`}</div>
        ) : null}
        <div className={classNames(styles.tips, isBigCard && styles.bigCardTips)}>
          {getDurationPeriod(data.commodityConfig)}
        </div>
        {data.promotionBanner ? <div className={styles.tag}>{data.promotionBanner}</div> : null}
      </>
    );
  };

  const renderProductContent = (data: Product) => {
    if (type === 'svip') {
      return renderBatchType(data);
    }
    // 美豆卡片
    if (curTab === TabType.MEIDOU) {
      return renderMeidouType(data);
    }

    // 张数卡片
    if (curTab === TabType.CUTOUT) {
      return renderConsumableType(data);
    }

    // 团队会员商品卡片
    if (subscribeType === SubscribeType.SUBSCRIPTION && isTeamSubscription) {
      return renderTeamSubscriptionType(data as GroupProduct);
    }

    // 个人版会员商品卡片
    return renderSubscriptionType(data);
  };

  const renderCard = (data: Product, index: number) => {
    return (
      <div
        className={classNames(
          styles.card,
          isBigCard && styles.bigCard,
          activeIndex === index ? styles.cardActived : ''
        )}
        onClick={() => handleSelectChange(index)}
      >
        {renderProductContent(data)}
      </div>
    );
  };

  const isSkeleton = !Object.keys((products && products[0]) || {}).length;

  return (
    <div className={classNames('vip-product-list', styles.module)}>
      <div className={classNames('swiper-container-new', isBigCard && 'big-card-container')}>
        <div className="swiper-wrapper">
          {products.length
            ? products?.map((v, index) => {
                return (
                  <div key={`${v.productId}_${index}`} className="swiper-slide">
                    {renderCard(v, index)}
                  </div>
                );
              })
            : Array.from({ length: 4 }, () => ({})).map((v, index) => {
                return (
                  <div
                    key={`skeleton_${index}`}
                    className={classNames('swiper-slide', styles.skeletonSlide)}
                  >
                    <div className={styles.skeleton}></div>
                  </div>
                );
              })}
        </div>
      </div>
      {renderArrow(!isSkeleton)}
    </div>
  );
};

export default React.memo(ProductList);
